import { ORBIT_REQUESTS_PATH, ORBIT_SESSIONS_PATH } from 'api/orbit';
import i18n from 'utils/i18n';

import { TOrbit } from '../../i18n/TModules';
import { OrbitSession, OrbitTurn } from '../../types';
import { OrbitAction, OrbitSelectRequest } from '../types';
import {
  ORBIT_L4_DEMO_SESSION_ID,
  ORBIT_L4_DEMO_TITLE,
  ORBIT_L4_DEMO_TURN_ID_BASE,
  isOrbitL4DemoTurnId
} from './constants';
import { demoFlowFor } from './fixtures';
import { OUT_OF_SCOPE_REPLY } from './fixtures/outOfScope';
import { IDemoFlow, IDemoReply } from './fixtures/types';

/** How long an asked turn and a confirming turn stay pending, so the working states can be seen. */
export const DEMO_ANSWER_DELAY_MS = 1500;
export const DEMO_CONFIRM_DELAY_MS = 2500;

export interface IDemoResponse {
  status: number;
  data: unknown;
}

interface IDemoRecord {
  /** The turn as it reads once it is done. */
  final: OrbitTurn;
  readyAt: number;
  /** The action a confirming turn carries while it runs. */
  runningAction?: OrbitAction;
  flow?: IDemoFlow;
  /** The confirming turn this proposal already produced: a second confirm answers with it. */
  confirmedTurnId?: number;
}

const EMPTY_ANSWER = {
  scope: '',
  evidence: [],
  confidence: '',
  data_quality: '',
  restrictions: '',
  next_step: '',
  visualization: 'none' as const
};

const EXPIRED = {
  status: 410,
  data: { code: 41001, message: 'This proposal changed or expired. Ask again.' }
};

const NOT_FOUND = { status: 404, data: { code: 40410, message: 'Orbit request not found' } };

let records = new Map<number, IDemoRecord>();
let nextId = ORBIT_L4_DEMO_TURN_ID_BASE;
let session: OrbitSession | null = null;

const iso = (time: number) => new Date(time).toISOString();

const completedTurn = (
  id: number,
  question: string,
  reply: IDemoReply,
  now: number
): OrbitTurn => ({
  id,
  session_id: ORBIT_L4_DEMO_SESSION_ID,
  state: 'completed',
  outcome: reply.outcome,
  question,
  context: {},
  metric_results: [],
  answer: { answer: reply.answer, ...EMPTY_ANSWER },
  report_download_id: null,
  feedback: null,
  created_at: iso(now),
  ...(reply.action === undefined ? {} : { action: reply.action })
});

const add = (record: IDemoRecord): OrbitTurn => {
  records.set(record.final.id, record);
  return record.final;
};

const seed = (now: number) => {
  if (records.size > 0) {
    return;
  }
  nextId += 1;
  // No question bubble, and the one outcome the panel draws as bare prose (a refusal with no action):
  // an `answered` turn would be drawn as an analysis card.
  add({
    final: completedTurn(
      nextId,
      '',
      { outcome: 'action_refused', answer: i18n.t(`${TOrbit}.layer4.demo.intro`) },
      now
    ),
    readyAt: now
  });
};

/** A turn as the server would serve it right now: pending until its answer is due. */
const read = (record: IDemoRecord, now: number): OrbitTurn => {
  if (now >= record.readyAt) {
    return record.final;
  }
  const { action, ...rest } = record.final;
  const pending: OrbitTurn = {
    ...rest,
    state: record.readyAt - now > DEMO_ANSWER_DELAY_MS / 2 ? 'queued' : 'processing',
    outcome: null,
    answer: {}
  };
  if (record.runningAction !== undefined) {
    pending.action = {
      ...record.runningAction,
      state: pending.state === 'queued' ? 'claimed' : 'executing'
    };
  }
  return pending;
};

export const demoSessionRecord = (now = Date.now()): OrbitSession => {
  if (session === null) {
    session = {
      session_id: ORBIT_L4_DEMO_SESSION_ID,
      title: ORBIT_L4_DEMO_TITLE,
      pinned: false,
      pinned_at: null,
      last_activity_at: iso(now),
      workspace: null,
      created_at: iso(now)
    };
  }
  return session;
};

const ask = (question: string, now: number): IDemoResponse => {
  const flow = demoFlowFor(question);
  // A proposal's 15 minutes run from when it is made, which is when the turn completes.
  const readyAt = now + DEMO_ANSWER_DELAY_MS;
  nextId += 1;
  const turn = add({
    final: completedTurn(nextId, question, flow?.reply(readyAt) ?? OUT_OF_SCOPE_REPLY, now),
    readyAt,
    flow
  });
  return { status: 202, data: { data: read(records.get(turn.id) as IDemoRecord, now), meta: {} } };
};

const select = (id: number, body: OrbitSelectRequest, now: number): IDemoResponse => {
  const record = records.get(id);
  const action = record?.final.action;
  if (record === undefined || action?.state !== 'awaiting_selection' || !record.flow?.select) {
    return {
      status: 422,
      data: { code: 4221, message: 'This turn has no pick waiting for a selection' }
    };
  }
  const candidate = action.candidates.find(
    entry => entry.id === body.target_id && entry.step_id === body.step_id
  );
  if (candidate === undefined) {
    return {
      status: 422,
      data: { code: 4221, message: 'target_id is not one of the candidates offered for this step' }
    };
  }
  if (!candidate.eligible) {
    return {
      status: 422,
      data: { code: 4221, message: 'target_id names a candidate that cannot be selected' }
    };
  }
  const reply = record.flow.select(candidate, now);
  record.final = {
    ...record.final,
    action: { ...action, state: reply.action?.state ?? 'awaiting_confirmation' }
  };
  nextId += 1;
  const turn = add({
    final: completedTurn(nextId, `Selected: ${candidate.name} (#${candidate.id})`, reply, now),
    readyAt: now,
    flow: record.flow
  });
  return { status: 200, data: { data: turn, meta: {} } };
};

const confirm = (id: number, now: number): IDemoResponse => {
  const record = records.get(id);
  const action = record?.final.action;
  if (record?.confirmedTurnId !== undefined) {
    const earlier = records.get(record.confirmedTurnId) as IDemoRecord;
    return { status: 202, data: { data: read(earlier, now), meta: {} } };
  }
  if (record === undefined || action === undefined || action.state !== 'awaiting_confirmation') {
    return EXPIRED;
  }
  if (action.expires_at !== null && Date.parse(action.expires_at) <= now) {
    return EXPIRED;
  }
  const result = record.flow?.confirm?.(action);
  if (result === undefined || result === 'expired') {
    return EXPIRED;
  }
  const { target } = action.steps[0];
  nextId += 1;
  const confirming = add({
    final: completedTurn(
      nextId,
      `Confirmed: ${action.steps[0].label ?? ''} ${target.name}${
        target.id === undefined ? '' : ` (#${target.id})`
      }`,
      result,
      now
    ),
    readyAt: now + DEMO_CONFIRM_DELAY_MS,
    runningAction: action
  });
  record.confirmedTurnId = confirming.id;
  // Every turn of one action is served with that action's latest state.
  record.final = { ...record.final, action: result.action ?? { ...action, state: 'completed' } };
  return {
    status: 202,
    data: { data: read(records.get(confirming.id) as IDemoRecord, now), meta: {} }
  };
};

const listTurns = (now: number): IDemoResponse => {
  seed(now);
  const turns = Array.from(records.values())
    .map(record => read(record, now))
    .sort((a, b) => b.id - a.id);
  return {
    status: 200,
    data: {
      data: turns,
      meta: {
        size: 25,
        page: 1,
        current_page: 1,
        per_page: 25,
        prev_page: null,
        next_page: null,
        total_count: turns.length,
        count: turns.length,
        total_pages: 1
      }
    }
  };
};

const parseBody = (data: unknown): Record<string, unknown> => {
  if (typeof data === 'string' && data !== '') {
    try {
      return JSON.parse(data);
    } catch (error) {
      return {};
    }
  }
  return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
};

const TURN_PATH = new RegExp(
  `^${ORBIT_REQUESTS_PATH}/(\\d+)(?:/(select|confirm|handoff_result))?$`
);
const SESSION_PATH = `${ORBIT_SESSIONS_PATH}/${ORBIT_L4_DEMO_SESSION_ID}`;

const pathAndQuery = (url: string) => new URL(url, 'http://orbit.demo');

/*
  While a demo turn's builder is open (`holdDemoModuleSaves`), every dashboard write it makes (the
  module create, an update on the real record it edits, a maker-checker request) is answered here,
  so a demo save never changes anything on the tenant. Orbit's own calls and the session keep-alive
  are not module writes.
*/
const DEMO_RECORD_ID_BASE = 990000;
let moduleSaveHolds = 0;
let nextRecordId = DEMO_RECORD_ID_BASE;

/** Arms the module-save mock for one open demo builder; returns its release. */
export const holdDemoModuleSaves = (): (() => void) => {
  moduleSaveHolds += 1;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      moduleSaveHolds -= 1;
    }
  };
};

const DASH_PREFIX = '/v4/dash/';
const PASS_THROUGH = ['/v4/dash/orbit/', '/v4/dash/user_sessions'];

const isModuleWrite = (method: string, pathname: string): boolean =>
  moduleSaveHolds > 0 &&
  method !== 'get' &&
  pathname.startsWith(DASH_PREFIX) &&
  !PASS_THROUGH.some(prefix => pathname.startsWith(prefix));

const moduleWriteResponse = (pathname: string): IDemoResponse => {
  const ids = pathname
    .split('/')
    .filter(segment => /^\d+$/.test(segment))
    .map(Number);
  if (ids.length === 0) {
    nextRecordId += 1;
    return { status: 201, data: { data: { id: nextRecordId }, meta: {} } };
  }
  return { status: 200, data: { data: { id: ids[0] }, meta: {} } };
};

const ENTITY_NOUN: Record<string, string> = {
  audience: 'audience',
  campaign: 'campaign',
  reward: 'reward',
  rule: 'rule',
  loyalty_program: 'loyalty programme'
};

const handoff = (id: number, body: Record<string, unknown>, now: number): IDemoResponse => {
  const record = records.get(id);
  const action = record?.final.action;
  if (record === undefined || action === undefined || action.state !== 'awaiting_confirmation') {
    return { status: 409, data: { code: 409, message: 'This proposal is no longer open.' } };
  }
  const resourceId = Number(body.resource_id);
  const entity = String(body.resource_type ?? action.steps[0].target.entity);
  const [first] = action.steps;
  const handedOff: OrbitAction = {
    ...action,
    state: 'handed_off',
    steps: [
      {
        ...first,
        status: 'handed_off',
        target: { ...first.target, entity, id: resourceId },
        result: { id: resourceId, via: 'builder' },
        link: { entity, id: resourceId, subtype: first.target.subtype }
      }
    ]
  };
  const noun = ENTITY_NOUN[entity] ?? entity;
  record.final = { ...record.final, action: handedOff };
  nextId += 1;
  const turn = add({
    final: completedTurn(
      nextId,
      `Created in builder: ${noun} ${first.target.name}`,
      {
        outcome: 'action_handoff',
        answer: `Created the ${noun} ${first.target.name} (#${resourceId}) in the builder.`,
        action: handedOff
      },
      now
    ),
    readyAt: now
  });
  // The doc's response is the new turn's id and action only; the client reads the rest.
  return { status: 200, data: { data: { id: turn.id, action: turn.action }, meta: {} } };
};

/** Whether a request belongs to the demo chat and must be answered here, never sent. */
export const isDemoRequest = (method: string, url: string, data: unknown): boolean => {
  const { pathname, searchParams } = pathAndQuery(url);
  if (pathname === SESSION_PATH || isModuleWrite(method, pathname)) {
    return true;
  }
  const turn = TURN_PATH.exec(pathname);
  if (turn !== null) {
    return isOrbitL4DemoTurnId(Number(turn[1]));
  }
  if (pathname !== ORBIT_REQUESTS_PATH) {
    return false;
  }
  if (method === 'get') {
    return searchParams.get('session_id') === ORBIT_L4_DEMO_SESSION_ID;
  }
  return parseBody(data).session_id === ORBIT_L4_DEMO_SESSION_ID;
};

export const handleDemoRequest = (
  method: string,
  url: string,
  data: unknown,
  now = Date.now()
): IDemoResponse => {
  const { pathname } = pathAndQuery(url);

  if (isModuleWrite(method, pathname)) {
    return moduleWriteResponse(pathname);
  }

  if (pathname === SESSION_PATH) {
    if (method === 'delete') {
      resetOrbitL4Demo();
      return { status: 204, data: null };
    }
    if (method === 'patch') {
      session = { ...demoSessionRecord(now), ...parseBody(data) } as OrbitSession;
    }
    return { status: 200, data: { data: demoSessionRecord(now), meta: {} } };
  }

  const turn = TURN_PATH.exec(pathname);
  if (turn !== null) {
    const id = Number(turn[1]);
    if (turn[2] === 'select' && method === 'post') {
      return select(id, parseBody(data) as unknown as OrbitSelectRequest, now);
    }
    if (turn[2] === 'confirm' && method === 'post') {
      return confirm(id, now);
    }
    if (turn[2] === 'handoff_result' && method === 'post') {
      return handoff(id, parseBody(data), now);
    }
    const record = records.get(id);
    return record === undefined
      ? NOT_FOUND
      : { status: 200, data: { data: read(record, now), meta: {} } };
  }

  if (method === 'post') {
    return ask(String(parseBody(data).question ?? ''), now);
  }
  return listTurns(now);
};

export const resetOrbitL4Demo = (): void => {
  records = new Map();
  nextId = ORBIT_L4_DEMO_TURN_ID_BASE;
  nextRecordId = DEMO_RECORD_ID_BASE;
  moduleSaveHolds = 0;
  session = null;
};
